import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  fetchGetEmployees,
  fetchGetProjects,
  createEmployeeMapping,
} from "../../services/empMappingService";
import { Employee, Project, FormState } from "../../Interface/EmpMapping";
import MuiAlert, { AlertProps } from "@mui/material/Alert";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddMapping: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    employeeCode: "",
    employeeName: "",
    projectName: "",
    rate: "",
    utilization: "",
    startDate: "",
    endDate: "",
    type: "",
  });

  const [employeeList, setEmployeeList] = useState<Employee[]>([]);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  //Concatenate First Name & Last Name
  const uniqueEmployeeNames = useMemo(() => {
    const seen = new Set();
    const fullNames = employeeList
      .map((e) => `${e.firstName} ${e.lastName}`)
      .filter((name) => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      });
    return fullNames.sort((a, b) => a.localeCompare(b));
  }, [employeeList]);

  const uniqueEmployeeCodes = useMemo(() => {
    const seen = new Set();
    return employeeList
      .map((e) => e.employeeCode)
      .filter((code) => {
        if (seen.has(code)) return false;
        seen.add(code);
        return true;
      });
  }, [employeeList]);

  const loadInitialData = async () => {
    try {
      const empRes = await fetchGetEmployees();
      setEmployeeList(empRes || []);
      const projRes = await fetchGetProjects();
      setProjectList(projRes || []);
    } catch (error) {
      console.error("Error loading data", error);
    }
  };

  useEffect(() => {
    const emp = employeeList.find(
      (e) => `${e.firstName} ${e.lastName}` === form.employeeName
    );
    if (emp) {
      setForm((prev) => ({
        ...prev,
        employeeCode: emp.employeeCode,
        projectName: emp.projectName,
      }));
    }
  }, [form.employeeName]);

  useEffect(() => {
    const emp = employeeList.find((e) => e.employeeCode === form.employeeCode);
    if (emp) {
      setForm((prev) => ({
        ...prev,
        employeeName: `${emp.firstName} ${emp.lastName}`,
        projectName: emp.projectName,
      }));
    }
  }, [form.employeeCode]);

  // Handle Change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Restrict to numeric input for rate and utilization
    if ((name === "rate" || name === "utilization") && value !== "") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    if (name === "employeeName") {
      const emp = employeeList.find(
        (e) => `${e.firstName} ${e.lastName}` === value
      );
      if (emp) {
        setForm((prev) => ({
          ...prev,
          employeeName: value,
          employeeCode: emp.employeeCode,
          projectName: emp.projectName || "",
        }));
      } else {
        setForm((prev) => ({ ...prev, employeeName: value }));
      }
    } else if (name === "employeeCode") {
      const emp = employeeList.find((e) => e.employeeCode === value);
      if (emp) {
        setForm((prev) => ({
          ...prev,
          employeeCode: value,
          employeeName: `${emp.firstName} ${emp.lastName}`,
          projectName: emp.projectName || "",
        }));
      } else {
        setForm((prev) => ({ ...prev, employeeCode: value }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  //Handle Save
  const handleSave = () => {
    // Validation: Numeric check
    if (isNaN(Number(form.rate)) || isNaN(Number(form.utilization))) {
      setSnackbar({ open: true, message: "Rate and Utilization must be valid numbers" });
      return;
    }

    // Validation: Start date must be < End date
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setSnackbar({ open: true, message: "Please select valid Start and End Dates" });
      return;
    }

    if (start >= end) {
      setSnackbar({ open: true, message: "Start Date must be before End Date" });
      return;
    }

    setConfirmOpen(true);
  };

  //Confirm Save
  const confirmSave = async () => {
    setConfirmOpen(false);
    try {
      const selectedEmp = employeeList.find(
        (e) => e.employeeCode === form.employeeCode
      );
      const selectedProject = projectList.find(
        (p) => p.projectName === form.projectName
      );
      if (!selectedEmp || !selectedProject) {
        setSnackbar({ open: true, message: "Please check if all fields are filled" });
        return;
      }
      const now = new Date().toISOString();
      const payload = {
        createdOn: now,
        createdBy: 1,
        modifiedOn: now,
        modifiedBy: 0,
        isDeleted: false,
        isActive: true,
        employeeId: selectedEmp.employeeId,
        projectId: selectedProject.projectId,
        currencyId: 2,
        rate: parseFloat(form.rate),
        utilization: parseFloat(form.utilization),
        startDate: form.startDate,
        endDate: form.endDate,
        type: form.type.toLowerCase(),
      };

      const res = await createEmployeeMapping(payload);

      let message = "Mapping Created Successfully";
      if (res?.data?.message) message = res.data.message;
      else if (res?.message) message = res.message;
      else if (typeof res === "string") message = res;

      setSnackbar({ open: true, message });

      setTimeout(() => {
        navigate("/EmpMapping");
      }, 1500);
    } catch (error) {
      console.error("Error creating employee mapping:", error);
      setSnackbar({ open: true, message: "Check the utilization" });
    }
  };

  // Field for SearchDrawer
  const fields = [
    {
      name: "employeeName",
      label: "Employee Name",
      type: "dropdown",
      options: uniqueEmployeeNames,
    },
    {
      name: "employeeCode",
      label: "Employee Code",
      type: "dropdown",
      options: uniqueEmployeeCodes,
    },
    {
      name: "projectName",
      label: "Project Name",
      type: "dropdown",
      options: projectList.map((p) => p.projectName),
    },
    {
      name: "type",
      label: "Type",
      type: "dropdown",
      options: ["Full-time", "Part-time", "Consultant"],
    },
    { name: "rate", label: "Rate ($)", type: "text" },
    { name: "utilization", label: "Utilization (%)", type: "text" },
    { name: "startDate", label: "Start Date", type: "date" },
    { name: "endDate", label: "End Date", type: "date" },
  ];

  return (
    <Box sx={{ bgcolor: "#f0f2f5", p: 3, minHeight: "100vh" }}>
      <Card elevation={1} sx={{ bgcolor: "#fff", width: "calc(100% - 32px)", m: 2, p: 3, minHeight: "80vh" }}>
        <Typography variant="h6" mb={3}>Add Project Mapping</Typography>
        <Grid container spacing={2} maxWidth="600px" direction="column">
          {fields.map(({ name, label, type, options }, index) => (
            <Grid item key={name}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography sx={{ width: "200px", textAlign: "right", pr: 1 }}>{label}</Typography>
                {type === "dropdown" ? (
                  <TextField
                    select
                    name={name}
                    value={form[name as keyof FormState]}
                    onChange={handleChange}
                    size="small"
                    sx={{ flex: 1 }}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                  >
                    {options?.map((option: string) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    name={name}
                    type={type}
                    value={form[name as keyof FormState]}
                    onChange={handleChange}
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={type === "date" ? { shrink: true } : undefined}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box mt={3} display="flex" gap={2}>
          <Button variant="contained" color="primary" onClick={handleSave}>SAVE</Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate("/EmpMapping")}>BACK</Button>
        </Box>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>Are you sure you want to save this mapping?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmSave} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={
            snackbar.message.toLowerCase().includes("success")
              ? "success"
              : snackbar.message.toLowerCase().includes("error")
                ? "error"
                : "warning"
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddMapping;
